/**
 * 状态工具函数
 * 统一管理所有状态相关的工具函数和配置
 */

/**
 * 状态配置接口
 */
export interface StatusConfig {
  /** 状态颜色 */
  color: string
  /** 状态文本 */
  text: string
  /** 状态图标（可选） */
  icon?: string
  /** 状态类型（用于样式区分） */
  type?: 'default' | 'success' | 'warning' | 'error' | 'processing'
}

/**
 * 项目状态映射
 */
export const PROJECT_STATUS_MAP: Record<string, StatusConfig> = {
  draft: {
    color: 'default',
    text: '草稿',
    type: 'default'
  },
  pending: {
    color: 'orange',
    text: '待执行',
    type: 'warning'
  },
  processing: {
    color: 'blue',
    text: '处理中',
    type: 'processing'
  },
  in_progress: {
    color: 'blue',
    text: '进行中',
    type: 'processing'
  },
  completed: {
    color: 'green',
    text: '已完成',
    type: 'success'
  },
  failed: {
    color: 'red',
    text: '失败',
    type: 'error'
  },
  expired: {
    color: 'orange',
    text: '已过期',
    type: 'warning'
  },
  cleaned: {
    color: 'default',
    text: '已清理',
    type: 'default'
  }
}

/**
 * 任务状态映射
 */
export const TASK_STATUS_MAP: Record<string, StatusConfig> = {
  pending: {
    color: 'orange',
    text: '待处理',
    type: 'warning'
  },
  in_progress: {
    color: 'blue',
    text: '进行中',
    type: 'processing'
  },
  completed: {
    color: 'green',
    text: '已完成',
    type: 'success'
  },
  cancelled: {
    color: 'red',
    text: '已取消',
    type: 'error'
  }
}

/**
 * 用户状态映射
 */
export const USER_STATUS_MAP: Record<string, StatusConfig> = {
  active: {
    color: 'green',
    text: '活跃',
    type: 'success'
  },
  inactive: {
    color: 'default',
    text: '非活跃',
    type: 'default'
  },
  suspended: {
    color: 'red',
    text: '暂停',
    type: 'error'
  },
  banned: {
    color: 'red',
    text: '已封禁',
    type: 'error'
  }
}

/**
 * 文档状态映射
 */
export const DOCUMENT_STATUS_MAP: Record<string, StatusConfig> = {
  draft: {
    color: 'default',
    text: '草稿',
    type: 'default'
  },
  uploading: {
    color: 'blue',
    text: '上传中',
    type: 'processing'
  },
  extracting: {
    color: 'blue',
    text: '提取中',
    type: 'processing'
  },
  translating: {
    color: 'blue',
    text: '翻译中',
    type: 'processing'
  },
  completed: {
    color: 'green',
    text: '已完成',
    type: 'success'
  },
  failed: {
    color: 'red',
    text: '失败',
    type: 'error'
  }
}

/**
 * 视频状态映射
 */
export const VIDEO_STATUS_MAP: Record<string, StatusConfig> = {
  draft: {
    color: 'default',
    text: '草稿',
    type: 'default'
  },
  uploading: {
    color: 'blue',
    text: '上传中',
    type: 'processing'
  },
  extracting: {
    color: 'blue',
    text: '提取字幕中',
    type: 'processing'
  },
  editing: {
    color: 'blue',
    text: '编辑中',
    type: 'processing'
  },
  translating: {
    color: 'blue',
    text: '翻译中',
    type: 'processing'
  },
  completed: {
    color: 'green',
    text: '已完成',
    type: 'success'
  },
  failed: {
    color: 'red',
    text: '失败',
    type: 'error'
  }
}

/**
 * 获取状态配置
 * @param status 状态值
 * @param type 状态类型：'project' | 'task' | 'user' | 'document' | 'video'
 * @returns 状态配置对象
 */
export function getStatusConfig(
  status: string,
  type: 'project' | 'task' | 'user' | 'document' | 'video' = 'project'
): StatusConfig {
  let statusMap: Record<string, StatusConfig>
  
  switch (type) {
    case 'project':
      statusMap = PROJECT_STATUS_MAP
      break
    case 'task':
      statusMap = TASK_STATUS_MAP
      break
    case 'user':
      statusMap = USER_STATUS_MAP
      break
    case 'document':
      statusMap = DOCUMENT_STATUS_MAP
      break
    case 'video':
      statusMap = VIDEO_STATUS_MAP
      break
    default:
      statusMap = PROJECT_STATUS_MAP
  }
  
  return statusMap[status] || {
    color: 'default',
    text: status,
    type: 'default'
  }
}

/**
 * 获取状态颜色
 * @param status 状态值
 * @param type 状态类型
 * @returns 状态颜色
 */
export function getStatusColor(
  status: string,
  type: 'project' | 'task' | 'user' | 'document' | 'video' = 'project'
): string {
  return getStatusConfig(status, type).color
}

/**
 * 获取状态文本
 * @param status 状态值
 * @param type 状态类型
 * @returns 状态文本
 */
export function getStatusText(
  status: string,
  type: 'project' | 'task' | 'user' | 'document' | 'video' = 'project'
): string {
  return getStatusConfig(status, type).text
}

/**
 * 获取状态类型
 * @param status 状态值
 * @param type 状态类型
 * @returns 状态类型
 */
export function getStatusType(
  status: string,
  type: 'project' | 'task' | 'user' | 'document' | 'video' = 'project'
): 'default' | 'success' | 'warning' | 'error' | 'processing' {
  return getStatusConfig(status, type).type || 'default'
}

/**
 * 注册自定义状态映射
 * @param type 状态类型
 * @param statusMap 状态映射对象
 */
export function registerStatusMap(
  type: string,
  statusMap: Record<string, StatusConfig>
): void {
  // 这里可以根据需要扩展，暂时不实现动态注册
  console.warn('registerStatusMap is not implemented yet')
}

